import React from 'react';
import ReactDOM from 'react-dom/client';
import { Analytics } from '@vercel/analytics/react';
import App from '@/App';
import '@/styles/index.css';
import { initializeWebVitals } from '@/utils/webVitals';
import { captureGoogleAdsAttribution } from '@/utils/Analytics';
import { isPrerendering } from '@/utils/prerender';
import { isProductionHost } from '@/utils/productionHost';

// Stale-deploy self-healing: after a deploy, clients from the previous build
// request old hashed chunks, receive the SPA-fallback index.html instead, and
// die with "SyntaxError: Unexpected token '<'" (264 Rollbar hits on Jul 11's
// 4-deploy day). One reload picks up the new build. The session-scoped 30s
// guard prevents a reload loop when the failure is persistent (real outage),
// letting the true error surface.
const reloadOnceForStaleChunk = () => {
  // NEVER reload during the build-time prerender: a reload mid-snapshot kills
  // the whole build ("Execution context was destroyed, most likely because of
  // a navigation") whenever a chunk request hiccups on the local prerender
  // server — the source of the intermittent Vercel production build failures.
  // __PRERENDER_INJECTED is set before any app script runs (vite.config.ts).
  if ((window as unknown as { __PRERENDER_INJECTED?: unknown }).__PRERENDER_INJECTED) return false;
  const KEY = 'swtpa_chunk_reload_at';
  const last = Number(sessionStorage.getItem(KEY) || 0);
  if (Date.now() - last < 30000) return false;
  sessionStorage.setItem(KEY, String(Date.now()));
  window.location.reload();
  return true;
};

// The signature of a stale chunk: HTML served for a script (Unexpected token '<'),
// or a dynamic-import / chunk-load failure.
const isStaleChunkError = (v: unknown): boolean => {
  const s = String((v as { message?: string })?.message ?? v ?? '');
  const name = (v as { name?: string })?.name ?? '';
  return (
    name === 'ChunkLoadError' ||
    s.includes("Unexpected token '<'") ||
    s.includes('Failed to fetch dynamically imported module') ||
    s.includes('error loading dynamically imported module') ||
    /Loading (CSS )?chunk [\w-]+ failed/i.test(s) ||
    s.includes("expected expression, got '<'") // Firefox variant
  );
};

// Vite's dedicated event for a failed module preload (the common case).
window.addEventListener('vite:preloadError', (event) => {
  if (reloadOnceForStaleChunk()) event.preventDefault();
});
// The cases vite:preloadError misses: the entry script itself parsing HTML, and
// dynamic imports that reject/throw straight to window (SyntaxError on <).
window.addEventListener('error', (event) => {
  if (isStaleChunkError(event.error) || isStaleChunkError(event.message)) {
    if (reloadOnceForStaleChunk()) event.preventDefault();
  }
});
window.addEventListener('unhandledrejection', (event) => {
  if (isStaleChunkError(event.reason)) {
    if (reloadOnceForStaleChunk()) event.preventDefault();
  }
});

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);

// SECURITY: Environment check moved to secure logger
// (Development debug only - no production logging)

// Analytics bootstrap. These lived in src/main.tsx, which NOTHING imports —
// index.html loads this file. So from the day main.tsx was written until
// 2026-08-22 none of it ran in production: no Core Web Vitals were ever
// recorded, `localStorage.google_ads_attribution` was never written (so
// getGoogleAdsAttribution() always returned null and every gclid/campaign_source
// on our ecommerce events was undefined — we bought Google Ads with click
// attribution silently disabled), and Vercel Web Analytics never mounted.
//
// Gated on isPrerendering(): the build-time prerenderer runs this bundle in a
// headless browser against 127.0.0.1, and un-gated analytics there both pollutes
// prod data and can serialize state into the shipped HTML (see utils/prerender.ts).
if (!isPrerendering()) {
  captureGoogleAdsAttribution(); // must run on first load, before any navigation
  initializeWebVitals();
}

root.render(
  <React.StrictMode>
    <App />
    {/* Vercel Web Analytics — server-side pageviews, only collects on the
        production deployment. Cleaner geo/path/referrer than the GA4 property,
        which mixes web + iOS + bots. */}
    {isProductionHost() && <Analytics />}
  </React.StrictMode>
);

// SEO KEYSTONE: the build-time prerenderer (vite.config.ts) decides when to
// snapshot a route entirely NODE-SIDE — its pageHandler awaits the route's
// react-helmet <link rel="canonical"> via page.waitForSelector. No in-page
// readiness signal is needed or wanted here: in-page timers/events get throttled
// in the prerenderer's background tabs and hung the build.
